import { useState } from "react"
import StartTimerButton from "../../time/StartTimerButton"
import { Box, Card, Chip, Stack, Tooltip, Typography } from "@mui/material"
import SubtaskIcon from "@mui/icons-material/SubdirectoryArrowRightOutlined"
import { MOTION, PRIORITY_COLORS, STATUS_FALLBACK_COLORS, transition } from "../../theme"
import { rise } from "../../motion"

/**
 * The board.
 *
 * Drag and drop with the HTML5 API rather than a library: the interaction is one
 * card into one column, and a drag-and-drop dependency is a large amount of
 * bundle and API surface for that. It also degrades honestly — a card is still
 * clickable, and the detail drawer can change the status.
 */
export default function TaskBoard({ tasks, statuses, onMove, onOpen, canMove }) {
  const [dragging, setDragging] = useState(null)
  const [overColumn, setOverColumn] = useState(null)

  const columns = statuses.length > 0 ? statuses : [{ id: 2, name: "todo", label: "To Do" }]

  return (
    <Box sx={{ display: "flex", gap: 1.5, overflowX: "auto", pb: 1, alignItems: "flex-start" }}>
      {columns.map((status) => {
        const columnTasks = tasks.filter((t) => Number(t.status_id) === Number(status.id))
        const color = status.color || STATUS_FALLBACK_COLORS[status.name] || "#8a8f98"
        const isOver = overColumn === status.id

        return (
          <Box
            key={status.id}
            onDragOver={(event) => {
              if (!canMove || !dragging) return
              // Required: without preventDefault the drop event never fires and
              // the column silently refuses every card.
              event.preventDefault()
              setOverColumn(status.id)
            }}
            onDragLeave={() => setOverColumn((current) => (current === status.id ? null : current))}
            onDrop={(event) => {
              event.preventDefault()
              setOverColumn(null)
              if (!canMove || !dragging) return
              if (Number(dragging.status_id) !== Number(status.id)) onMove(dragging.id, status.id)
              setDragging(null)
            }}
            sx={{
              width: 300,
              flexShrink: 0,
              bgcolor: isOver ? "action.selected" : "action.hover",
              borderRadius: 2,
              p: 1,
              transition: transition("background-color", MOTION.fast),
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1, py: 0.75 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color }} />
              <Typography variant="body2" fontWeight={650} sx={{ flex: 1 }}>
                {status.label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {columnTasks.length}
              </Typography>
            </Stack>

            <Stack spacing={1} sx={{ minHeight: 40 }}>
              {/* A column with nothing in it used to be a blank rounded well.
                  Beside three full columns that reads as a load failure, not as
                  "nothing is done yet" — and it is the Done column that is empty
                  most often, on exactly the board somebody is checking progress
                  on. The placeholder also says what the column is for, which is
                  the one thing a board never explains. */}
              {columnTasks.length === 0 ? (
                <Box
                  sx={{
                    p: "18px 10px",
                    textAlign: "center",
                    fontSize: 12,
                    color: "text.secondary",
                    border: 1,
                    borderStyle: "dashed",
                    borderColor: "divider",
                    borderRadius: (theme) => `${theme.shape.borderRadius}px`,
                  }}
                >
                  Drop a task here to mark it {status.label.toLowerCase()}
                </Box>
              ) : null}

              {columnTasks.map((task, index) => (
                <Card
                  key={task.id}
                  draggable={canMove}
                  onDragStart={() => setDragging(task)}
                  onDragEnd={() => {
                    setDragging(null)
                    setOverColumn(null)
                  }}
                  onClick={() => onOpen(task.id)}
                  sx={{
                    p: 1.25,
                    cursor: canMove ? "grab" : "pointer",
                    opacity: dragging?.id === task.id ? 0.4 : 1,
                    "&:hover": { borderColor: "primary.main" },
                    ...rise(index),
                  }}
                >
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
                    {/* A step inside something, said on the card. Steps are not
                        filtered off the board — one assigned to you is yours,
                        and hiding it would lose it — so a card that is part of
                        something larger has to say so rather than sit among the
                        tasks as though it were one. The table view has carried
                        this mark since sub-tasks existed; the board had not. */}
                    {task.parent_task_id ? (
                      <Tooltip title={task.parent_title ? `A step inside “${task.parent_title}”` : "A step inside another task"}>
                        <SubtaskIcon sx={{ fontSize: 14, color: "text.disabled", flexShrink: 0 }} />
                      </Tooltip>
                    ) : null}
                    <Typography variant="caption" sx={{ fontFamily: "monospace", color: "text.secondary" }}>
                      {task.task_code}
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    {/* Starting a timer is the most common thing done to a card,
                        so it is on the card rather than two clicks into a drawer. */}
                    <StartTimerButton
                      icon
                      taskId={task.id}
                      description={task.title}
                    />
                    <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: PRIORITY_COLORS[task.priority] }} />
                  </Stack>

                  <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.35 }}>
                    {task.title}
                  </Typography>

                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {/* How far through its own steps this one is — the figure
                        that makes a card with ten steps behind it readable
                        without opening it. */}
                    {task.subtask_total > 0 ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`${task.subtask_done}/${task.subtask_total} steps`}
                        sx={{ height: 18, fontSize: 10 }}
                      />
                    ) : null}
                    {task.project_code ? (
                      <Chip size="small" label={task.project_code} sx={{ height: 18, fontSize: 10 }} />
                    ) : null}
                    {task.client_label && task.client_id !== "0" ? (
                      <Chip size="small" variant="outlined" label={task.client_label} sx={{ height: 18, fontSize: 10 }} />
                    ) : null}
                    {task.assignee_name ? (
                      <Chip size="small" variant="outlined" label={task.assignee_name} sx={{ height: 18, fontSize: 10 }} />
                    ) : null}
                  </Stack>
                </Card>
              ))}
            </Stack>
          </Box>
        )
      })}
    </Box>
  )
}
